import React, { useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { DiffContent } from '@atlassian/bitkit-diff';
import {
  isSameCommentPermalink,
  updateHashInURL,
} from '@atlassian/bitkit-diff/exports/diff-permalink';

import { CommentThread } from 'src/components/conversation/src/components/comment-tree';
import { toConversationId } from 'src/components/conversation-provider/utils/to-conversation';
import { withConversationAnalytics } from 'src/components/editor/with-analytics';
import {
  Conversation,
  Props as ConversationProps,
} from 'src/containers/conversation/conversation';
import { useConversationsContext } from 'src/contexts/conversations-context';
import { useFlag } from 'src/hooks/flag';
import { onPermalinkHashChange } from 'src/redux/pull-request/actions';
import { getContainerId } from 'src/redux/pull-request/selectors';
import history from 'src/router/history';
import { getUserAiOnboardingFlagDismissed } from 'src/sections/global/components/flags/user-ai-onboarding/user-ai-onboarding-flag';
import { getIsAIEnabledForWorkspace } from 'src/selectors/feature-selectors';
import { getRepositoryAccessLevel } from 'src/selectors/repository-selectors';
import { canModerateComments } from 'src/utils/moderate-comments';

import { usePullRequestCommentActions } from '../../hooks/conversation/pull-request-comment-actions';
import { useCodeSuggestions } from '../../hooks/conversation/use-code-suggestions';

import { AfterCommentItems } from './after-comment-items';

// For comments inside pull request files, we want the max level of comment nesting
// to be 1 - i.e. the root level comments and then 1 level of reply underneath the
// root level comments.
const MAX_CONVERSATION_NESTING = 1;

// We intentionally omit these props to block overrides for consistency
type Props = Omit<
  ConversationProps,
  'dataProviders' | 'objectId' | 'provider' | 'conversationsContext'
> & {
  editorPopupsMountPoint?: HTMLElement | null;
  /** When true, renders only the like button on comments while hiding the create task and create Jira isue actions */
  showLikeActionOnly?: boolean;
  diffLine?: DiffContent;
};

const BasePullRequestConversation = (props: Props) => {
  const {
    commentThreads,
    editorPopupsMountPoint,
    canCollapseThread,
    showLikeActionOnly,
    diffLine,
    ...otherProps
  } = props;
  const dispatch = useDispatch();

  const firstThread: CommentThread | undefined = commentThreads[0];
  const containerId = useSelector(getContainerId);

  const workspaceAiEnabled = useSelector(getIsAIEnabledForWorkspace);
  const { showFlagComponent } = useFlag({ enableForPdvs: true });

  const {
    conversationProvider,
    additionalConversationProviders,
    renderAdditionalActions,
  } = useConversationsContext();

  const repositoryAccessLevel = useSelector(getRepositoryAccessLevel);

  const renderAdditionalCommentActions = usePullRequestCommentActions(
    commentThreads,
    showLikeActionOnly
  );

  const handleOnCommentPermalinkClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>, commentId: number) => {
      event.preventDefault();
      updateHashInURL(`comment-${commentId}`, history);
      if (isSameCommentPermalink(commentId)) {
        dispatch(onPermalinkHashChange(`comment-${commentId}`));
      }
      if (otherProps.onCommentPermalinkClick) {
        otherProps.onCommentPermalinkClick(event, commentId);
      }
    },
    [dispatch, otherProps]
  );

  const {
    extensionProviders,
    codeSuggestionsExtensionHandler,
    editorPlugins,
    getSuggestions,
  } = useCodeSuggestions(diffLine);

  return (
    <Conversation
      canModerateComments={canModerateComments(repositoryAccessLevel)}
      commentThreads={commentThreads}
      id={firstThread ? toConversationId(firstThread.topComment) : undefined}
      maxCommentNesting={MAX_CONVERSATION_NESTING}
      onCommentPermalinkClick={handleOnCommentPermalinkClick}
      renderAdditionalCommentActions={
        renderAdditionalActions ? renderAdditionalCommentActions : undefined
      }
      renderAfterComment={AfterCommentItems}
      {...otherProps}
      dataProviders={additionalConversationProviders}
      objectId={containerId}
      onEditorOpen={() => {
        if (!getUserAiOnboardingFlagDismissed() && workspaceAiEnabled) {
          showFlagComponent('user-ai-onboarding');
        }
      }}
      provider={conversationProvider as any}
      canCollapseThread={canCollapseThread}
      canResolveThread={otherProps.canResolveThread}
      popupsMountPoint={editorPopupsMountPoint}
      extensionProviders={extensionProviders}
      codeSuggestionsExtensionHandler={codeSuggestionsExtensionHandler}
      editorPlugins={editorPlugins}
      getSuggestions={getSuggestions}
    />
  );
};

export const PullRequestConversation = withConversationAnalytics(
  React.memo(BasePullRequestConversation)
);

export const NewPullRequestConversation = React.memo((props: Props) => {
  return <PullRequestConversation {...props} isExpanded />;
});
